import { Fragment, useEffect, useMemo, useState } from 'react'
import CustomToolTip from '../../../../../../CommonComponents/CustomToolTip'
import { PlusSvg } from '../../../../../../CommonComponents/svgs/Svgs'
import useUrlNavigation from '../../../../../hooks/useUrlNavigation'
import useUrlParams from '../../../../../hooks/useUrlParams'
import { ScheduleMenu, get_navigator_button_by_dashboard } from '../../utils'
import './LeftSidebar.scss'
import useCustomSchedule from '../../../../../hooks/useCustomSchedule'
import useScenario from '../../../../../hooks/useScenario'
import { ScheduleData, ScheduleMenuOption } from '../../../../../types'
import { OverlayTrigger } from 'react-bootstrap'
import GroupButtonList from '../../../../../../CommonComponents/GroupButtonList'
import { getInitialCustomScheduleFormData, ScheduleMenuList } from '../../../../../utils'
import { SCENARIO_DEPLOYMENT_TYPES } from '../../../../../../simulate/constants'
import { formatScheduleDurationDisplay, isValidationSchedule } from './scheduleRowUtils'

const LeftSidebar = () => {

    const { scenario } = useScenario()
    const { navigate_to } = useUrlNavigation()
    const { tab_name, dashboard } = useUrlParams()
    const { onboardScheduleDetails, setOnboardScheduleDetails, setCustomScheduleModalData, onMenuClick } = useCustomSchedule()
    const isValidateSummary =
        (scenario?.scenarioDeploymentType ?? '').toUpperCase() === SCENARIO_DEPLOYMENT_TYPES.VALIDATE &&
        dashboard === 'summary'

    const [openMenu, setOpenMenu] = useState<string | boolean>(false)
    const [openOverlay, setOpenOverlay] = useState(false)
    const [expandExecuted, setExpandExecuted] = useState(false)

    const deploymentType = useMemo(() => scenario?.scenarioDeploymentType, [scenario?.scenarioDeploymentType])
    const scheduleData = useMemo(() => onboardScheduleDetails.scheduleData, [onboardScheduleDetails.scheduleData])
    const selectedSchedule = useMemo(() => onboardScheduleDetails.selectedSchedule, [onboardScheduleDetails.selectedSchedule])
    const executedSchedule = useMemo(() => scheduleData?.executed || [], [scheduleData?.executed])

    useEffect(() => {
        const clickOutside = (e: MouseEvent) => {
            if (e.target instanceof Node) {
                const overlay = document.querySelector('.overlay__menu__container')
                if (openOverlay && overlay && !overlay.contains(e.target as Node)) {
                    setOpenOverlay(false)
                }
            }
        }
        document.addEventListener('mousedown', clickOutside)
        return () => {
            document.removeEventListener('mousedown', clickOutside)
        }
    }, [openOverlay])

    const onOverlayMenuClick = (menu: ScheduleMenuOption) => {
        const initialData = getInitialCustomScheduleFormData(menu)
        setCustomScheduleModalData(initialData)
    }

    const onScheduleClick = (schedule: ScheduleData) => {
        const filterTask = (schedule?.scheduledTasks || []).filter(task => task?.commandsList?.length > 0)
        setOnboardScheduleDetails((prev) => ({
            ...prev,
            selectedSchedule: schedule,
            selectedTask: filterTask?.[0] || null,
            selectedTelecommand: filterTask?.[0]?.commandsList?.[0] || null,
        }))
    }

    return (
        <div className='left__sidebar__container'>
            
            <div className='left__sidebar__body'>
                {['active', 'planned', 'available'].map((item) => {
                    const showButton = item === 'available'
                    const data: ScheduleData[] = scheduleData[item]
                    const showButtonGroup = item !== 'active'
                    const hiddenButtons = item === 'planned' ? ['Edit'] : []
                    return (
                        <Fragment key={item}>
                            <div className='section__header__container'>
                                <div className='section__header__title'>{item} Schedule</div>
                                {showButton && !isValidateSummary &&
                                    <OverlayTrigger show={openOverlay} trigger={['click']} placement='right'
                                        overlay={
                                            <div className='overlay__menu__container'>
                                                {ScheduleMenu.map(menu => {
                                                    const Icon = menu.icon
                                                    return (
                                                        <button key={menu?.value} className='overlay__menu__item'
                                                            onClick={(() => {
                                                                onOverlayMenuClick(menu.value as ScheduleMenuOption);
                                                            })}
                                                        >
                                                            <span className='button__label'>{menu.label}</span>
                                                            <div className='button__icon'> <Icon /></div>
                                                        </button>
                                                    )
                                                })}
                                            </div>
                                        }
                                    >
                                        <button
                                            className='section__header__button'
                                            onClick={() => {
                                                setOpenOverlay(prev => !prev)
                                            }}
                                        >
                                            <CustomToolTip title='Add New' placement='top'>
                                                <PlusSvg />
                                            </CustomToolTip>
                                        </button>
                                    </OverlayTrigger>
                                }
                            </div>
                            <div className={`section__body__container ${item}`}>
                                {data?.length === 0 && <span className='placeholder__container'>There is no schedule available here.</span>}
                                {data?.map((schedule) => {
                                    const isActive = selectedSchedule?.scheduleId === schedule?.scheduleId
                                    const isExpired = schedule?.status === 'EXPIRED'
                                    const isValidation = isValidationSchedule(schedule, scenario)
                                    const computedHiddenButtons = isValidation
                                        ? ['Edit', 'Export']
                                        : (isExpired ? [...hiddenButtons, 'Edit'] : hiddenButtons)
                                    return (
                                        <div className={`section__details__container ${isActive ? 'active' : ''} ${isExpired ? 'expired' : ''}`} key={schedule.scheduleId}
                                            onClick={() => onScheduleClick(schedule)}
                                        >
                                            <div className='section__details__header'>
                                                <div className='section__details__header__title'>
                                                    <CustomToolTip title={schedule?.scheduleName ?? ''} placement='top'>
                                                        {schedule.scheduleName}
                                                    </CustomToolTip>
                                                </div>
                                                <span className='section__details__header__time'>
                                                    {isExpired ? 'Expired' : formatScheduleDurationDisplay(schedule)}
                                                </span>
                                            </div>
                                            <div className='section__details__body__container'>
                                                <div className='section__details__body'>
                                                    <span className='section__details__body__title'>{schedule?.startTimeISO8601}</span>
                                                </div>
                                                {showButtonGroup &&
                                                    <GroupButtonList
                                                        id={schedule.scheduleId as string}
                                                        open={openMenu}
                                                        toggle={(value) => {
                                                            setOpenMenu(value)
                                                        }}
                                                        hiddenButtons={computedHiddenButtons}
                                                        buttons={ScheduleMenuList}
                                                        onClick={(btn) => {
                                                            onMenuClick(btn, 'readonly', 'schedule', {
                                                                name: schedule?.scheduleName as string,
                                                                scheduleId: schedule?.scheduleId as string,
                                                            })
                                                        }}
                                                    >
                                                        <i className='fe fe-more-vertical opacity-75 icon__button' />
                                                    </GroupButtonList>
                                                }
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        </Fragment>
                    )
                })}
            </div>
            <div className={`left__sidebar__footer ${expandExecuted ? 'active' : ''}`}>
                <div className='section__header__container header__button' onClick={() => setExpandExecuted(!expandExecuted)}>
                    <span className='section__header__title'>Executed Schedule</span>
                    <span className='section__header__title'>{executedSchedule.length}</span>
                </div>
                <div className={`executed__section__body__container`}>
                    {executedSchedule.length === 0 && <span className='placeholder__container'>There is no executed schedule here.</span>}
                    {executedSchedule.map((schedule: ScheduleData) => {
                        const isActive = selectedSchedule?.scheduleId === schedule?.scheduleId
                        const isValidation = isValidationSchedule(schedule, scenario)
                        return (
                            <div className={`section__details__container ${isActive ? 'active' : ''}`} key={schedule.scheduleId}
                                onClick={() => onScheduleClick(schedule)}
                            >
                                <div className='section__details__header'>
                                    <div className='section__details__header__title'>
                                        <CustomToolTip title={schedule?.scheduleName ?? ''} placement='top'>
                                            {schedule.scheduleName}
                                        </CustomToolTip>
                                    </div>
                                    <span className='section__details__header__title'>Completed</span>
                                </div>
                                <div className='section__details__body__container'>
                                    <div className='section__details__body'>
                                        <span className='section__details__body__title'>{schedule?.startTimeISO8601}</span>
                                    </div>
                                    <GroupButtonList
                                        id={schedule.scheduleId as string}
                                        open={openMenu}
                                        toggle={(value) => {
                                            setOpenMenu(value)
                                        }}
                                        hiddenButtons={isValidation ? ['Edit', 'Export'] : ['Edit']}
                                        buttons={ScheduleMenuList}
                                        onClick={(btn) => {
                                            onMenuClick(btn, 'readonly', 'schedule', {
                                                name: schedule?.scheduleName as string,
                                                scheduleId: schedule?.scheduleId as string,
                                            })
                                        }}
                                    >
                                        <i className='fe fe-more-vertical opacity-75 icon__button' />
                                    </GroupButtonList>
                                </div>
                            </div>
                        )
                    })}
                </div>
            </div>
        </div>
    )
}

export default LeftSidebar